import type { MechanicDefinition } from "./types";

export interface ActionTemplate {
  id: string;
  label: string;
  icon?: string;
  category: "combat" | "social" | "exploration" | "magic" | "survival" | "custom";
  description: string;
  requires_target?: boolean;
  requires_detail?: boolean;
  detail_prompt?: string; // Hint for what the text field should say
  stat_used?: string; // Which stat this typically uses for checks
  tool_hint?: string; // Maps directly to a tool name for faster routing
  available_when?: string[]; // Context tags that must be present (e.g. ["combat"])
  unavailable_when?: string[]; // Context tags that block this action
}

export const DEFAULT_ACTION_TEMPLATES: ActionTemplate[] = [
  // Combat
  { id: "melee_attack", label: "Melee Attack", icon: "Swords", category: "combat", description: "Strike a target with a melee weapon", requires_target: true, stat_used: "STR", tool_hint: "combat_action", available_when: ["combat"] },
  { id: "ranged_attack", label: "Ranged Attack", icon: "Target", category: "combat", description: "Fire at a target with a ranged weapon", requires_target: true, stat_used: "DEX", tool_hint: "combat_action", available_when: ["combat"] },
  { id: "defend", label: "Defend", icon: "Shield", category: "combat", description: "Brace for incoming attack, gain defensive bonus", stat_used: "CON", tool_hint: "combat_action", available_when: ["combat"] },
  { id: "flee", label: "Flee", icon: "Wind", category: "combat", description: "Attempt to escape from combat", stat_used: "DEX", tool_hint: "combat_action", available_when: ["combat"] },
  // Social
  { id: "persuade", label: "Persuade", icon: "MessageCircle", category: "social", description: "Convince someone through reason or charm", requires_target: true, requires_detail: true, detail_prompt: "What are you trying to convince them of?", stat_used: "CHA", tool_hint: "skill_check" },
  { id: "intimidate", label: "Intimidate", icon: "AlertTriangle", category: "social", description: "Threaten or pressure someone into compliance", requires_target: true, stat_used: "CHA", tool_hint: "skill_check" },
  { id: "deceive", label: "Deceive", icon: "Eye", category: "social", description: "Lie or mislead someone", requires_target: true, requires_detail: true, detail_prompt: "What lie are you telling?", stat_used: "CHA", tool_hint: "skill_check" },
  { id: "barter", label: "Barter", icon: "Coins", category: "social", description: "Negotiate a trade or deal", requires_target: true, requires_detail: true, detail_prompt: "What are you offering/asking for?", stat_used: "CHA", tool_hint: "skill_check" },
  // Exploration
  { id: "investigate", label: "Investigate", icon: "Search", category: "exploration", description: "Examine something closely for clues", requires_detail: true, detail_prompt: "What are you investigating?", stat_used: "INT", tool_hint: "skill_check" },
  { id: "perceive", label: "Perceive", icon: "Eye", category: "exploration", description: "Use your senses to detect hidden things", stat_used: "WIS", tool_hint: "skill_check" },
  { id: "sneak", label: "Sneak", icon: "Footprints", category: "exploration", description: "Move stealthily to avoid detection", stat_used: "DEX", tool_hint: "skill_check" },
  { id: "pick_lock", label: "Pick Lock", icon: "KeyRound", category: "exploration", description: "Attempt to open a locked door or container", stat_used: "DEX", tool_hint: "skill_check", requires_target: true },
  { id: "navigate", label: "Navigate", icon: "Compass", category: "exploration", description: "Find your way through unfamiliar terrain", stat_used: "WIS", tool_hint: "skill_check" },
  // Survival
  { id: "forage", label: "Forage", icon: "Leaf", category: "survival", description: "Search for food, water, or useful materials", stat_used: "WIS", tool_hint: "skill_check" },
  { id: "rest", label: "Rest", icon: "Moon", category: "survival", description: "Take a short or long rest to recover", unavailable_when: ["combat"] },
  { id: "craft", label: "Craft", icon: "Hammer", category: "survival", description: "Create or repair an item", requires_detail: true, detail_prompt: "What are you crafting?", stat_used: "INT", tool_hint: "skill_check" },
  // Magic
  { id: "cast_spell", label: "Cast Spell", icon: "Sparkles", category: "magic", description: "Cast a known spell", requires_detail: true, detail_prompt: "Which spell?", stat_used: "INT", tool_hint: "cast_spell" },
  { id: "use_ability", label: "Use Ability", icon: "Zap", category: "magic", description: "Use a class or special ability", requires_detail: true, detail_prompt: "Which ability?" },
];

export const actionTemplates: MechanicDefinition = {
  id: "action_templates",
  disabled: false,
  name: "Action Templates",
  description: "Structured action system with predefined action types. Players pick from available actions with optional parameters. Freeform text is validated against templates.",
  icon: "LayoutGrid",
  aiHints: {
    purpose: "Provides clickable action buttons (melee attack, persuade, search, etc.) so players don't need to type everything.",
    whenToUse: "Any game — especially useful for mobile players or games with clear action categories.",
    synergies: ["combat", "skill_checks", "magic_system", "survival"],
    conflicts: [],
    prerequisites: [],
    tags: ["actions", "ui", "buttons", "templates", "input"],
    complexity: 1,
    exampleConfig: { show_categories: true },
    stateVariables: [],
  },
  category: "systems",
  configSchema: [
    {
      key: "mode",
      label: "Template Mode",
      type: "select",
      options: [
        { value: "hybrid", label: "Hybrid (buttons + freeform with validation)" },
        { value: "strict", label: "Strict (only template actions allowed)" },
        { value: "suggestions", label: "Suggestions (buttons shown, freeform always accepted)" },
      ],
      default: "hybrid",
      description: "How strictly to enforce action templates",
    },
    {
      key: "show_unavailable",
      label: "Show Unavailable Actions",
      type: "boolean",
      default: true,
      description: "Show greyed-out actions that aren't available in the current context",
    },
    {
      key: "custom_templates",
      label: "Additional Custom Templates (JSON)",
      type: "text",
      default: "[]",
      description: "JSON array of custom ActionTemplate objects to add alongside defaults",
    },
  ],
  systemPromptSection: (config) => {
    const mode = config.mode || "hybrid";
    const modeDesc: Record<string, string> = {
      hybrid: "Players use predefined action buttons OR type freeform. If freeform, validate it matches an available action template. If it doesn't fit ANY template, respond in-character explaining why that action isn't possible and suggest valid alternatives.",
      strict: "Players MUST use predefined action templates. If a freeform message doesn't match any template, reject it with an in-character explanation: 'You consider [action], but realize [reason why not possible]. Perhaps you could [suggest valid alternatives].' NEVER execute an action that doesn't fit a template.",
      suggestions: "Action templates are shown as suggestions but freeform actions are always accepted. Use templates to guide players toward interesting choices.",
    };

    // Parse custom templates
    let customTemplates: ActionTemplate[] = [];
    try {
      customTemplates = JSON.parse(config.custom_templates || "[]");
    } catch { /* ignore */ }

    const allTemplates = [...DEFAULT_ACTION_TEMPLATES, ...customTemplates];
    const templateList = allTemplates.map(t => 
      `  • ${t.id}: "${t.label}" (${t.category}) — ${t.description}${t.stat_used ? ` [${t.stat_used}]` : ""}${t.tool_hint ? ` → ${t.tool_hint}` : ""}${t.requires_target ? " [needs target]" : ""}${t.requires_detail ? ` [needs detail: "${t.detail_prompt}"]` : ""}${t.available_when?.length ? ` [only during: ${t.available_when.join(",")}]` : ""}${t.unavailable_when?.length ? ` [blocked during: ${t.unavailable_when.join(",")}]` : ""}`
    ).join("\n");

    return `## ACTION TEMPLATES (${mode.toUpperCase()} MODE)
- ${modeDesc[mode]}
- Available action templates:
${templateList}
- Context-aware: Check current context_tags to determine which actions are available/blocked.
- When a player uses a template action, ALWAYS roll the appropriate skill check using the stat listed.
- When a template specifies a tool_hint, use that tool directly for faster resolution.
- When providing action results, reference the template name so the UI can highlight it.
- If the player's freeform text maps to a template, treat it as that template action.
- IMPORTANT: When responding, include available_actions in your state update with the action IDs currently valid for the player's situation.`;
  },
  widgets: [
    {
      id: "action_bar_inline",
      type: "inline",
      label: "Actions",
      icon: "LayoutGrid",
      component: "ActionTemplateBar",
      dataBindings: {
        contextTags: "state.context_tags",
        availableActions: "variables.available_actions",
      },
      visibility: "when_active",
      priority: 30,
    },
  ],
  tools: [
    {
      type: "function",
      function: {
        name: "validate_action",
        description: "Validate whether a player's action fits an available action template. Use when receiving freeform text to check if it maps to a valid template.",
        parameters: {
          type: "object",
          properties: {
            player_input: { type: "string", description: "What the player typed or selected" },
            matched_template: { type: "string", description: "The template ID this maps to, or 'none' if no match" },
            is_valid: { type: "boolean", description: "Whether this action is currently available" },
            rejection_reason: { type: "string", description: "If invalid, why (in-character)" },
            suggested_alternatives: { type: "array", items: { type: "string" }, description: "Template IDs the player could use instead" },
          },
          required: ["player_input", "matched_template", "is_valid"],
        },
      },
    },
  ],
};
